import { endpoints } from '@gopinhos/shared';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../../lib/api-client';
import { dateLocaleFor } from '../../lib/i18n';
import { Skeleton } from '../ui/skeleton';

interface HistoryEntry {
  id: string;
  fromStage: { id: string; slug: string; name: string } | null;
  toStage: { id: string; slug: string; name: string };
  movedBy: { id: string; fullName: string };
  note: string | null;
  durationSecondsInPrevious: number | null;
  movedAt: string;
}

export function History({ leadId }: { leadId: string }): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = dateLocaleFor(i18n.resolvedLanguage ?? i18n.language);
  const history = useQuery({
    queryKey: ['leadHistory', leadId],
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.leadHistory(leadId),
      }) as Promise<{ items: HistoryEntry[] }>,
  });

  if (history.isLoading) return <Skeleton className="h-32" />;
  return (
    <ol className="space-y-3 border-l-2 border-surface-200 pl-4">
      {(history.data?.items ?? []).map((entry) => (
        <li key={entry.id} className="relative">
          <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-brand-500 ring-4 ring-white" />
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="font-medium">{entry.movedBy.fullName}</span>
            <span className="text-surface-500">{t('history.moved')}</span>
            {entry.fromStage ? (
              <>
                <span className="rounded-sm bg-surface-100 px-1.5 py-0.5 text-xs">
                  {entry.fromStage.name}
                </span>
                <ArrowRight className="h-3 w-3 text-surface-400" />
              </>
            ) : (
              <span className="text-surface-500">{t('history.createdAt')}</span>
            )}
            <span className="rounded-sm bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
              {entry.toStage.name}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-surface-500">
            {formatDistanceToNow(new Date(entry.movedAt), { addSuffix: true, locale })}
            {entry.durationSecondsInPrevious !== null &&
              ` · ${humanDuration(entry.durationSecondsInPrevious)} ${t('history.previousIn')}`}
          </p>
          {entry.note && <p className="mt-1 text-sm text-surface-700">"{entry.note}"</p>}
        </li>
      ))}
    </ol>
  );
}

function humanDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.round((seconds / 3600) * 10) / 10}h`;
  return `${Math.round((seconds / 86_400) * 10) / 10}d`;
}
